"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.centsToNumberWithCommas = exports.convertToCents = exports.centsToDollars = exports.dollarsToCents = exports.removeDollarSignAndCommas = exports.parseCents = exports.formatDollarsFromCents = exports.formatNumberToUsd = exports.roundNumber = void 0;
const enums_1 = require("./enums");
const errors_1 = require("./errors");
const number_utils_1 = require("./number.utils");
const roundNumber = (number, roundingMethod) => {
    if (roundingMethod === enums_1.RoundingMethod.FLOOR) {
        return Math.floor(number);
    }
    return Math.round(number);
};
exports.roundNumber = roundNumber;
const formatNumberToUsd = (number, options) => {
    const { minimumFractionDigits = 0, roundingMethod = enums_1.RoundingMethod.ROUND, showCents = true, withDollarSign = true, } = options || {};
    const value = showCents ? number : (0, exports.roundNumber)(number, roundingMethod);
    const formatter = new Intl.NumberFormat('en-US', {
        currency: 'USD',
        minimumFractionDigits,
        style: withDollarSign ? 'currency' : 'decimal',
    });
    return formatter.format(value);
};
exports.formatNumberToUsd = formatNumberToUsd;
const formatDollarsFromCents = (amountInCents, options) => (0, exports.formatNumberToUsd)((0, number_utils_1.divideBy100)((0, exports.parseCents)(amountInCents)), options);
exports.formatDollarsFromCents = formatDollarsFromCents;
/**
 * Format cents function that takes in a number representing the amount of something
 * in cents and formats it according to our rules:
 *  1. Check for NaN
 *  2. We use floor to remove decimals. This is used when dealing with money
 *     amounts, and since all our money values are stored in cents as integers this
 *     will remove decimals that happen as a byproduct of a math operation.
 *
 * @param n cents to format
 * @returns formatted number
 */
const parseCents = (n) => {
    if (isNaN(n)) {
        throw (0, errors_1.createError)('CentsError', `Invalid number, n: ${n}`);
    }
    // To remove potential sign on 0 (-0 or +0) we always return 0 if the number
    // is 0 (this works because -0 === 0 === +0)
    return n === 0 ? 0 : parseInt(n.toFixed(2));
};
exports.parseCents = parseCents;
const removeDollarSignAndCommas = (input) => input.replace(/\$|,/g, '');
exports.removeDollarSignAndCommas = removeDollarSignAndCommas;
/**
 * Format dollars number to cents number. e.g. 1.00 -> 100
 * @param dollarsFloat dollars number to format
 * @returns cents number
 */
const dollarsToCents = (dollarsFloat) => (0, exports.parseCents)(dollarsFloat * 100);
exports.dollarsToCents = dollarsToCents;
/**
 * Format cents number to dollars number with 2 decimal places. e.g. 100 -> 1.00
 * @param centsFloat cents number to format
 * @returns dollars number
 */
const centsToDollars = (centsFloat) => {
    const dollarsFloat = centsFloat / 100;
    return parseFloat(dollarsFloat.toFixed(2));
};
exports.centsToDollars = centsToDollars;
/**
 * Format dollar string to cents number, or return null if the value isn't a valid number (empty, invalid string).
 * @param dollar dollar string or number to format. In case of string, it can have a dollar sign and commas. e.g. "$1,000.00"
 * @returns cents number
 */
const convertToCents = (dollars) => {
    if (!dollars || !dollars.trim()) {
        return null;
    }
    const stringValue = (0, exports.removeDollarSignAndCommas)(dollars.trim());
    const isValidNumber = /^-?\d*\.?\d*$/.test(stringValue);
    if (!isValidNumber) {
        return null;
    }
    const numericValue = Number.parseFloat(stringValue);
    return (0, exports.dollarsToCents)(numericValue);
};
exports.convertToCents = convertToCents;
/**
 * Convert a cents to a string with commas taking into account 2 decimal points.
 */
const centsToNumberWithCommas = (cents) => {
    const dollar = cents / 100;
    return dollar.toLocaleString('en', { maximumFractionDigits: 2 });
};
exports.centsToNumberWithCommas = centsToNumberWithCommas;
